/**
 * Read through cache
 * @param promiseMaker - Instance of $q, or bluebird, etc
 * @param keyFinder - Given a key, returns a promise to resolve it to a value.
 * @param keyMaker - Given parameters forwarded to get, return a string to be used as a cache key.
 *                   Optional, can be skipped if this is going to be an identity function.
 * @constructor
 */
var ReadThroughCache = function (telemetry, promiseMaker, keyResolver, keyMaker?) {
    var This = this;
    This._telemetry = telemetry;
    This._promiseMaker = promiseMaker;
    This._keyMaker = keyMaker || function (key) {return key;};
    This._keyResolver = keyResolver;
    This._cache = {};

    //Deferred promises to resolve once a key has been successfully resolved.
    This._pendingResolves = {};
};

/**
 * A out of cycle way to warm up the cache. This is best used to preload data into the cache for faster reads
 * later on
 */
ReadThroughCache.prototype.cacheWarmup = function (key, data) {
    var This = this;
    This._telemetry.debug("warmed key: " + key);
    This._cache[key] = data;
};

/**
 * When we know that the cached value is old, use this to cause the next lookup to trigger a key->value resolve.
 * @param key
 */
ReadThroughCache.prototype.cacheInvalidateKey = function (key) {
    var This = this;
    This._telemetry.debug("invalidated key: " + key);
    delete This._cache[key];
};

/**
 * Reset the cache. Forget all data.
 */
ReadThroughCache.prototype.cacheInvalidateAllKeys = function () {
    var This = this;
    var nKeys = Object.keys(This._cache);
    if (nKeys) {
        This._telemetry.debug("invalidated " + nKeys + " keys");
    }
    This._cache = {};
};

ReadThroughCache.prototype._makeKey = function (argumentsArray) {
    var This = this;
    var keyMaker = This._keyMaker;
    return keyMaker.apply(null, argumentsArray);
};

ReadThroughCache.prototype._resolveValue = function (key) {
    var This = this;
    var keyResolver = This._keyResolver;
    return keyResolver(key);
};

ReadThroughCache.prototype._makePromise = function (f) {
    var This = this;
    var promiseMaker = This._promiseMaker;
    return promiseMaker(f);
};

/**
 * Create a new resolve request for this key and add it to the pending resolves
 * @param key
 * @private
 */
ReadThroughCache.prototype._createPendingResolve = function (key) {
    var This = this;

    //Once the value is resolved we broadcast it to everyone in the queue for that key
    var onResolve = function (value) {
        This._telemetry.debug("miss key: " + key);
        This._cache[key] = value;
        var pendingResolves = This._pendingResolves[key];
        delete This._pendingResolves[key];
        pendingResolves.forEach(function (deferral) {
            deferral.resolve(value);
        });

    };


    //Once the value is rejected we broadcast to everyone in the queue for that key
    var onReject = function (err) {
        delete This._cache[key];
        var pendingResolves = This._pendingResolves[key];
        delete This._pendingResolves[key];
        pendingResolves.forEach(function (deferral) {
            deferral.reject(err);
        });
        delete This._pendingResolves[key];
    };

    //Create the queue and put the caller into it;
    var deferral = This._promiseMaker.defer();
    This._pendingResolves[key] = [deferral];

    //Kick off the resolution
    var valuePromise = This._resolveValue(key);
    valuePromise.then(onResolve, onReject);

    return deferral.promise;
};

/**
 * Read from the cache, if not present, return null
 * @returns {*}
 */
ReadThroughCache.prototype.get = function () {
    var This = this;
    var key = This._makeKey(arguments);
    return This._cache[key] || null;
};


/**
 * Read data from the cache. If not present resolve it.
 */
ReadThroughCache.prototype.find = function () {
    var This = this;
    var key = This._makeKey(arguments);

    /**
     * If we already have this in the cache, just return what we have!
     */
    if (key in This._cache) {
        This._telemetry.debug("hit key: " + key);
        var value = This._cache[key];
        return This._makePromise(function (resolve) {
            resolve(value);
        });
    }



    /**
     * Ok, we don't have it in the cache. Check if we are already resolving this key. If yes, get in line!
     *
     */
    if (key in This._pendingResolves) {
        var deferral = This._promiseMaker.defer();
        This._pendingResolves[key].push(deferral);
        return deferral.promise;
    }

    /**
     * Ok we are the first one, so we have to resolve and create a line for others to get into
     */
    return This._createPendingResolve(key);
};

export {ReadThroughCache};
